import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/platformAuthStore';
import { useProjectStore, type ProjectSummary } from '../stores/projectStore';
import { ProjectCard } from '../components/ProjectCard';
import { CreateProjectModal } from '../components/CreateProjectModal';
import { ImportProjectModal } from '../components/ImportProjectModal';
import { theme } from '../theme';

const styles = {
  container: {
    maxWidth: '960px',
    margin: '0 auto',
    padding: '32px 20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: theme.textPrimary,
  },
  usage: {
    fontSize: '13px',
    color: theme.textMuted,
    marginTop: '4px',
  },
  createBtn: {
    padding: '8px 20px',
    borderRadius: theme.radiusMd,
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    background: theme.accent,
    color: theme.accentText,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  empty: {
    textAlign: 'center' as const,
    color: theme.textMuted,
    padding: '60px 20px',
    fontSize: '15px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: theme.textPrimary,
    margin: '40px 0 16px',
  },
  loading: {
    textAlign: 'center' as const,
    color: theme.textMuted,
    padding: '60px 20px',
  },
  inviteRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    marginBottom: '8px',
    background: theme.bgPanel,
    border: `1px solid ${theme.border}`,
    borderRadius: theme.radiusLg,
    fontSize: '14px',
    color: theme.textPrimary,
  },
  inviteActions: {
    display: 'flex',
    gap: '8px',
  },
  declineBtn: {
    padding: '8px 20px',
    borderRadius: theme.radiusMd,
    border: `1px solid ${theme.border}`,
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    background: 'transparent',
    color: theme.textSecondary,
  },
  purgeInput: {
    padding: '6px 10px',
    borderRadius: theme.radiusMd,
    border: `1px solid ${theme.border}`,
    background: theme.bgInput,
    color: theme.textPrimary,
    fontSize: '13px',
  },
  error: {
    color: theme.error,
    marginBottom: '12px',
    fontSize: '13px',
  },
  banner: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    marginBottom: '24px',
    background: theme.warningBg,
    border: `1px solid ${theme.warning}`,
    borderRadius: theme.radiusLg,
    fontSize: '14px',
    color: theme.textPrimary,
  },
};

function VerifyBanner({ email }: { email: string }) {
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | string>('idle');

  const resend = async () => {
    setState('sending');
    const r = await useAuthStore.getState().resendVerification();
    setState(r.ok ? 'sent' : (r.error ?? 'Could not send the email'));
  };

  return (
    <div style={styles.banner}>
      <span>
        Verify your email to create games. We sent a link to <strong>{email}</strong>.
        {state === 'sent' && ' A new link is on its way.'}
        {state !== 'idle' && state !== 'sending' && state !== 'sent' && (
          <span style={{ color: theme.error }}> {state}</span>
        )}
      </span>
      <button style={styles.declineBtn} onClick={resend} disabled={state === 'sending'}>
        {state === 'sending' ? 'Sending...' : 'Resend email'}
      </button>
    </div>
  );
}

export function Dashboard() {
  const { fetchMe, user, notice, clearNotice } = useAuthStore();
  const {
    projects,
    sharedProjects,
    invites,
    blockedInvites,
    isLoading,
    fetchProjects,
    acceptInvite,
    declineInvite,
    unblockInvites,
    trash,
    fetchTrash,
    untrashProject,
    purgeProject,
    usage,
    fetchUsage,
  } = useProjectStore();
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [trashError, setTrashError] = useState('');
  const [purging, setPurging] = useState<string | null>(null);
  const [purgeName, setPurgeName] = useState('');

  const restoreFromTrash = async (projectId: string) => {
    setTrashError('');
    try {
      await untrashProject(projectId);
    } catch (err) {
      setTrashError(err instanceof Error ? err.message : 'Failed to restore the game');
    }
  };

  const purge = async (projectId: string) => {
    setTrashError('');
    try {
      await purgeProject(projectId, purgeName);
      setPurging(null);
      setPurgeName('');
    } catch (err) {
      setTrashError(err instanceof Error ? err.message : 'Failed to delete the game');
    }
  };

  const answerInvite = async (projectId: string, answer: 'accept' | 'decline' | 'block') => {
    setInviteError('');
    try {
      if (answer === 'accept') await acceptInvite(projectId);
      else await declineInvite(projectId, answer === 'block');
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to answer invite');
    }
  };

  const unblock = async (projectId: string) => {
    setInviteError('');
    try {
      await unblockInvites(projectId);
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to unblock invites');
    }
  };

  useEffect(() => {
    fetchMe();
    fetchProjects();
    fetchTrash().catch(() => {});
  }, [fetchMe, fetchProjects, fetchTrash]);

  // Every create, import, delete, restore and start or stop ends in a new
  // project or trash list, so that is when the usage line goes stale.
  useEffect(() => {
    fetchUsage().catch(() => {});
  }, [projects, trash, fetchUsage]);

  return (
    <div style={styles.container}>
      {notice && (
        <div style={styles.banner}>
          <span>{notice}</span>
          <button style={styles.declineBtn} onClick={clearNotice}>
            Dismiss
          </button>
        </div>
      )}
      {user && user.emailVerified === false && <VerifyBanner email={user.email} />}

      {invites.length > 0 && (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ ...styles.sectionTitle, marginTop: 0 }}>Invites</h2>
          {inviteError && <div style={styles.error}>{inviteError}</div>}
          {invites.map((inv) => (
            <div key={inv.projectId} style={styles.inviteRow}>
              <span>
                <strong>{inv.ownerUsername}</strong> invited you to collaborate on{' '}
                <strong>{inv.projectName}</strong>
              </span>
              <div style={styles.inviteActions}>
                <button
                  style={styles.createBtn}
                  onClick={() => answerInvite(inv.projectId, 'accept')}
                >
                  Accept
                </button>
                <button
                  style={styles.declineBtn}
                  onClick={() => answerInvite(inv.projectId, 'decline')}
                >
                  Decline
                </button>
                <button
                  style={styles.declineBtn}
                  onClick={() => answerInvite(inv.projectId, 'block')}
                >
                  Decline and block
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>My Projects</h1>
          {usage && (
            <div style={styles.usage}>
              {usage.projects.used} of {usage.projects.limit} games, {usage.storageMb.used} of{' '}
              {usage.storageMb.limit} MB, {usage.runningGames.used} of {usage.runningGames.limit}{' '}
              running
            </div>
          )}
        </div>
        <div style={styles.inviteActions}>
          <button style={styles.declineBtn} onClick={() => setShowImport(true)}>
            Import a game
          </button>
          <button style={styles.createBtn} onClick={() => setShowCreate(true)}>
            + New Project
          </button>
        </div>
      </div>

      {isLoading && projects.length === 0 ? (
        <div style={styles.loading}>Loading projects...</div>
      ) : projects.length === 0 ? (
        <div style={styles.empty}>
          No projects yet. Create your first game! New here?{' '}
          <Link to="/guide" style={{ color: theme.accent }}>
            Start with the guide.
          </Link>
        </div>
      ) : (
        <div style={styles.grid}>
          {projects.map((p: ProjectSummary) => (
            <ProjectCard key={p.id} project={p} />
          ))}
        </div>
      )}

      {sharedProjects.length > 0 && (
        <>
          <h2 style={styles.sectionTitle}>Shared with you</h2>
          <div style={styles.grid}>
            {sharedProjects.map((p) => (
              <ProjectCard key={p.id} project={p} />
            ))}
          </div>
        </>
      )}

      {blockedInvites.length > 0 && (
        <>
          <h2 style={styles.sectionTitle}>Blocked projects</h2>
          {invites.length === 0 && inviteError && <div style={styles.error}>{inviteError}</div>}
          {blockedInvites.map((b) => (
            <div key={b.projectId} style={styles.inviteRow}>
              <span>
                Invites from <strong>{b.projectName}</strong> ({b.ownerUsername}) are blocked
              </span>
              <button style={styles.declineBtn} onClick={() => unblock(b.projectId)}>
                Unblock
              </button>
            </div>
          ))}
        </>
      )}

      {trash.length > 0 && (
        <>
          <h2 style={styles.sectionTitle}>Trash</h2>
          {trashError && <div style={styles.error}>{trashError}</div>}
          {trash.map((t) => {
            const daysLeft = Math.max(0, Math.ceil((t.purgeAt - Date.now()) / 86_400_000));
            return (
              <div key={t.id} style={styles.inviteRow}>
                <span>
                  <strong>{t.name}</strong> is deleted for good in {daysLeft}{' '}
                  {daysLeft === 1 ? 'day' : 'days'}
                </span>
                {purging === t.id ? (
                  <div style={styles.inviteActions}>
                    <input
                      style={styles.purgeInput}
                      placeholder={`Type "${t.name}" to confirm`}
                      value={purgeName}
                      onChange={(e) => setPurgeName(e.target.value)}
                      autoFocus
                    />
                    <button
                      style={styles.declineBtn}
                      onClick={() => purge(t.id)}
                      disabled={purgeName !== t.name}
                    >
                      Delete forever
                    </button>
                    <button
                      style={styles.declineBtn}
                      onClick={() => {
                        setPurging(null);
                        setPurgeName('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div style={styles.inviteActions}>
                    <button style={styles.createBtn} onClick={() => restoreFromTrash(t.id)}>
                      Restore
                    </button>
                    <button style={styles.declineBtn} onClick={() => setPurging(t.id)}>
                      Delete forever
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {showCreate && <CreateProjectModal onClose={() => setShowCreate(false)} />}
      {showImport && <ImportProjectModal onClose={() => setShowImport(false)} />}
    </div>
  );
}
